import { useState, type MouseEvent } from 'react';
import { formatConversationDate, type Conversation, type ConversationsState } from '../../../../lib/conversations';
import { externalChannelOf } from '../../../../lib/external-channel';
import { useBasePath } from '../../../../lib/use-base-path';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useTranslation } from '../../../../lib/i18n';
import { conversationLink } from '../shared';
import { useConversationSwitch } from './use-conversation-switch';

// The rail shows the ten most recent threads; the rest live in the search dialog.
const RECENT_LIMIT = 10;

/** Pinned chats get their own section, so the recent list must EXCLUDE them — otherwise every pinned
 *  conversation renders twice, once in each section. This is also why the recent list is no longer sorted
 *  pinned-first: with a dedicated section above it, that ordering has nothing left to express. */
const isPinned = (c: Conversation): boolean => Boolean(c.pinned);

export interface RecentChatModel {
  conv: Conversation;
  /** The thread's own display name, or its date when it was never titled. */
  title: string;
  /** The Next page file, for `<Link href>`. */
  route: string;
  /** The URL the browser shows, for `<Link as>` — mount-prefixed. */
  path: string;
  isSelected: boolean;
  /** 'WhatsApp' / 'SMS' when the thread came in over an external channel. */
  channelBadge: string | null;
  onClick: (e: MouseEvent) => void;
}

export interface RecentChatsModel {
  chats: RecentChatModel[];
  open: boolean;
  toggle: () => void;
  label: string;
}

/** Which section a list is: the pinned group, or the recent group. They share every row behaviour and differ
 *  only in which conversations they take and what the header says, so one hook serves both. */
export type ChatSection = 'pinned' | 'recent';

// Chat History as a collapsible section: which threads it lists, whether it's open,
// and where each row goes (the thread swaps in place — see useConversationSwitch).
// Shared by the wide rail and the mobile drawer.
export function useRecentChats({
  state,
  selectedId,
  defaultOpen,
  section = 'recent',
}: {
  state: ConversationsState;
  selectedId: string | null;
  defaultOpen: boolean;
  /** Defaults to `recent` so every existing call site keeps its behaviour. */
  section?: ChatSection;
}): RecentChatsModel {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const switchConversation = useConversationSwitch();
  const [open, setOpen] = useState(defaultOpen);

  const all = state.kind === 'ready' ? state.conversations : [];
  // The pinned list is NOT capped: a pin has to keep a conversation reachable however old it gets, which is
  // the whole point of pinning one, and the recency cap is exactly what would take it away again.
  const conversations = section === 'pinned' ? all.filter(isPinned) : all.filter((c) => !isPinned(c)).slice(0, RECENT_LIMIT);
  // A WhatsApp GROUP conversation badges as "WhatsApp Group" (matching native's drawer.channel.*),
  // not plain "WhatsApp" — the DTO's `isWaGroup` distinguishes it. An EXTERNAL_AGENT conversation
  // badges with the platform's own name ("Claude Code" / "Gemini Enterprise") from the conversation
  // metadata, falling back to a generic label when the name wasn't stamped.
  const channelLabel = (conv: { communicationChannel?: string; isWaGroup?: boolean; agentPlatform?: { name: string } }): string | null => {
    const channel = externalChannelOf(conv.communicationChannel);
    if (channel === 'WHATSAPP') return conv.isWaGroup ? t('sidebar.channelBadge.whatsappGroup') : t('sidebar.channelBadge.whatsapp');
    if (channel === 'SMS') return t('sidebar.channelBadge.sms');
    if (channel === 'EXTERNAL_AGENT') return conv.agentPlatform?.name || t('sidebar.channelBadge.externalAgent');
    // Email / Telegram / Voice are named too (channels the user owns off-app), independent of the
    // read-only `externalChannelOf` set. APP / WEB / unknown get no sub-label — the chat lives here.
    if (conv.communicationChannel === 'EMAIL') return t('sidebar.channelBadge.email');
    if (conv.communicationChannel === 'TELEGRAM') return t('sidebar.channelBadge.telegram');
    if (conv.communicationChannel === 'VOICE') return t('sidebar.channelBadge.voice');
    return null;
  };

  return {
    label: t(section === 'pinned' ? 'sidebar.pinnedChats' : 'sidebar.recentChats'),
    open,
    toggle: () => setOpen((o) => !o),
    chats: conversations.map((conv) => {
      const link = conversationLink(basePath, conv.id);
      return {
        conv,
        title: conv.name || formatConversationDate(conv.createdDate),
        route: link.route,
        path: link.path,
        isSelected: conv.id === selectedId,
        channelBadge: channelLabel(conv),
        onClick: (e: MouseEvent) => {
          if (conv.id) {
            logPageClickBi({ page_name: 'menu', button_name: 'chat_history', origin: 'menu', conversation_id: conv.id });
          }
          switchConversation(conv.id, e);
        },
      };
    }),
  };
}
